/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Utils for text format
 *
 * @author Fedorov Maxim
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.0.1
 */

import moment, { Moment } from 'moment';

import { getTimeZone } from '../components/Table/helpers/getTimeZone';
import { EFormatData } from '../pages/PageDashboard/types/widgetTypes';

/**
 * Below is the function that performs the declension of numerals in javascript.
 * @number {number}
 * @titles {string}
 */
export const declensionOfNumerals = (number: number, titles: string[]): string => {
    try {
        const cases = [2, 0, 1, 1, 1, 2];
        return titles[number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]];
    } catch {
        return '';
    }
};

export const formatInquiries = (id: string): string => id.replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, '$1 ');

export const formatInquiryNumber = (id: string): string => id.replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, '$1-');

export const formatBalance = (num: number, precision?: number): string => {
    const balance = num ? num : 0;
    return balance
        .toFixed(precision !== undefined ? precision : 2)
        .toString()
        .replace(/[\.]/g, ',')
        .replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, '$1 ');
};

/**
 * Function removes insignificant trailing zeros from a number if the number has no fractional value
 * @example 100,00 => 100
 * @example 100,10 => 100,10
 */
export const formatBalanceWithoutTrailingZeros = (num: number, precision?: number): string => {
    return formatBalance(num, precision).replace(/,0+$/, '');
};

export const formatPrice = (price: number, currency: string) => `${Math.round(price * 100) / 100} ${currency}`;

export const formatPriceWithMeasureUnit = (price: number, currency: string, period: number, measureName: string) =>
    `${formatPrice(price, currency)}/${period > 1 ? period + ' ' + measureName : measureName}`;

interface IFormatDateOptions {
    format?: string;
    skipZone?: boolean;
}

export const formatDate = (date: string, options: IFormatDateOptions = {}): string => {
    const format = options.format || EFormatData.FullDateWithTime;
    return `${moment(date).format(format)}${options.skipZone ? '' : ` ${getTimeZone(date)}`}`;
};

export const formatDateRangeToDDMMYYYY = (startDate: Moment, endDate: Moment, monthDeclension: string[]): string => {
    // date from
    const dayFrom = startDate.format(EFormatData.OnlyDayFull);
    const monthFrom = monthDeclension[+startDate.format(EFormatData.OnlyMonth) - 1];
    const yearFrom = startDate.format(EFormatData.OnlyYearFull);
    // date to
    const dayTo = endDate.format(EFormatData.OnlyDayFull);
    const monthTo = monthDeclension[+endDate.format(EFormatData.OnlyMonth) - 1];
    const yearTo = endDate.format(EFormatData.OnlyYearFull);

    let labelFrom = dayFrom;
    const labelTo = `${dayTo} ${monthTo} ${yearTo}`;

    if (monthFrom !== monthTo) {
        labelFrom += ` ${monthFrom}`;
    }
    if (yearFrom !== yearTo) {
        labelFrom += ` ${yearFrom}`;
    }

    return `${labelFrom} - ${labelTo}`;
};

export const formatDateToMMYYYY = (date?: Moment, monthDeclension?: string[]): string => {
    if (!date || !monthDeclension) return '';

    const month = monthDeclension[+date.format(EFormatData.OnlyMonth) - 1];
    const year = date.format(EFormatData.OnlyYearFull);

    return `${month} ${year}`;
};

export const formatDateRangeToMMYYYY = (startDate: Moment, endDate: Moment, monthDeclension: string[]): string => {
    // date from
    const monthFrom = monthDeclension[+startDate.format(EFormatData.OnlyMonth) - 1].toLowerCase();
    const yearFrom = startDate.format(EFormatData.OnlyYearFull);
    // date to
    const monthTo = monthDeclension[+endDate.format(EFormatData.OnlyMonth) - 1].toLowerCase();
    const yearTo = endDate.format(EFormatData.OnlyYearFull);

    let labelFrom = '';
    const labelTo = `${monthTo} ${yearTo}`;

    if (monthFrom === monthTo && yearFrom === yearTo) {
        return labelTo;
    }

    labelFrom += monthFrom;

    if (yearFrom !== yearTo) {
        labelFrom += ` ${yearFrom}`;
    }

    return `${labelFrom} – ${labelTo}`;
};

export const getFileExtension = (fileName: string) => fileName?.split('.').pop()?.toLowerCase() || '';

export const getPrintableText = (text: string): string => text.replace(/(?:\r\n|\r|\n)/g, '<br />');
